import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const schedule = [
  { day: 'Monday', hours: '8:00 AM \u2013 8:00 PM' },
  { day: 'Tuesday', hours: '8:00 AM \u2013 8:00 PM' },
  { day: 'Wednesday', hours: '8:00 AM \u2013 8:00 PM' },
  { day: 'Thursday', hours: '8:00 AM \u2013 8:00 PM' },
  { day: 'Friday', hours: '8:00 AM \u2013 8:00 PM' },
  { day: 'Saturday', hours: '9:00 AM \u2013 6:00 PM' },
  { day: 'Sunday', hours: '10:00 AM \u2013 5:00 PM' },
];

export default function Hours() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  const getCurrentDay = useCallback(() => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[new Date().getDay()];
  }, []);

  const currentDay = getCurrentDay();
  const currentDayIdx = schedule.findIndex((s) => s.day === currentDay);

  // Auto-cycle weekly hours every 3 seconds
  useEffect(() => {
    if (isHovering) return;
    const interval = setInterval(() => {
      setActiveIdx((prev) => (prev + 1) % schedule.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [isHovering]);

  const todaySchedule = schedule.find((s) => s.day === currentDay);

  return (
    <section id="hours" className="py-16 md:py-24 bg-[#f8f8f7] w-full overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full">

        {/* Section header */}
        <div className="mb-10">
          <p className="text-xs font-semibold text-teal-600 uppercase tracking-[0.15em] mb-3">
            Hours &amp; Location
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-4">
            Visit Us <span className="text-teal-600">Today</span>
          </h2>
          <p className="text-gray-500 text-[0.94rem] leading-relaxed max-w-xl">
            Open 7 days a week with extended hours. Walk-ins are welcome \u2014 or call ahead to book your appointment.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10 items-start">

          {/* Left \u2014 Hours table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="flex-1 min-w-0"
          >
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {/* Weekly Hours Switcher Header */}
              <div
                className="bg-[#1b3330] px-5 py-4 relative overflow-hidden"
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold text-white/90 tracking-[0.12em] uppercase">
                    Weekly Hours
                  </h3>
                  {/* Day dots */}
                  <div className="flex items-center gap-1.5">
                    {schedule.map((item, idx) => (
                      <button
                        key={item.day}
                        onClick={() => setActiveIdx(idx)}
                        className={`w-1.5 h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                          activeIdx === idx
                            ? 'bg-white w-4'
                            : 'bg-white/30 hover:bg-white/50'
                        }`}
                        aria-label={`Show ${item.day} hours`}
                      />
                    ))}
                  </div>
                </div>

                {/* Cycling day/hours display */}
                <div className="mt-3 min-h-[44px] relative">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeIdx}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.35, ease: 'easeInOut' }}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2.5">
                        <div
                          className={`w-2 h-2 rounded-full flex-shrink-0 ${
                            currentDay === schedule[activeIdx].day
                              ? 'bg-teal-400'
                              : 'bg-white/40'
                          }`}
                        />
                        <span className="text-sm font-bold text-white">
                          {schedule[activeIdx].day}
                        </span>
                        {currentDay === schedule[activeIdx].day && (
                          <span className="text-[10px] bg-teal-500/20 text-teal-300 px-2 py-0.5 rounded-full font-medium border border-teal-500/30">
                            Today
                          </span>
                        )}
                      </div>
                      <span className="text-sm font-medium text-white/80">
                        {schedule[activeIdx].hours}
                      </span>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>

              {/* Today's Hours \u2014 Static Display */}
              <div className="bg-teal-50 border-b border-gray-100 px-5 py-4">
                <p className="text-[10px] font-semibold text-teal-600 uppercase tracking-[0.15em] mb-2">
                  Today&apos;s Hours
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-teal-600 text-sm"></i>
                    </div>
                    <div>
                      <span className="block text-sm font-bold text-teal-700">
                        {todaySchedule?.day}
                      </span>
                      <span className="block text-[10px] text-teal-500">
                        Open now
                      </span>
                    </div>
                  </div>
                  <span className="text-base font-semibold text-teal-700">
                    {todaySchedule?.hours}
                  </span>
                </div>
              </div>

              {/* Full schedule list */}
              <div className="divide-y divide-gray-100">
                {schedule.map((item) => {
                  const isToday = currentDay === item.day;
                  return (
                    <div
                      key={item.day}
                      className={`flex items-center justify-between px-5 py-3 transition-colors ${
                        isToday
                          ? 'bg-teal-50/50'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-2 h-2 rounded-full flex-shrink-0 ${
                            isToday ? 'bg-teal-500' : 'bg-gray-300'
                          }`}
                        />
                        <span
                          className={`text-sm font-bold ${
                            isToday ? 'text-teal-700' : 'text-gray-800'
                          }`}
                        >
                          {item.day}
                        </span>
                        {isToday && (
                          <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-medium">
                            Today
                          </span>
                        )}
                      </div>
                      <span
                        className={`text-sm font-medium ${
                          isToday ? 'text-teal-700' : 'text-gray-600'
                        }`}
                      >
                        {item.hours}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="px-5 py-4 border-t border-gray-100 bg-gray-50">
                <p className="text-xs text-gray-500 leading-relaxed flex items-start gap-2">
                  <i className="ri-information-line text-teal-500 text-sm flex-shrink-0 mt-px"></i>
                  Hours may vary on holidays. Please call ahead to confirm availability.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right \u2014 Contact info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1, ease: 'easeOut' }}
            className="lg:w-80 space-y-4 flex-shrink-0 w-full"
          >
            {/* Contact card */}
            <div className="bg-gray-900 rounded-lg p-6 text-white">
              <p className="text-xs font-semibold text-teal-400 uppercase tracking-widest mb-3">
                Get In Touch
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-white/10 rounded-md flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-teal-400 text-base"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Address</p>
                    <p className="text-sm text-white leading-snug">
                      8108 Yonge St Unit 3-4
                      <br />
                      Thornhill, ON L4J 1W4
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-white/10 rounded-md flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-teal-400 text-base"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Phone</p>
                    <a
                      href="tel:9057099985"
                      className="text-sm font-semibold text-white hover:text-teal-400 transition-colors cursor-pointer"
                    >
                      (905) 709-9985
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-white/10 rounded-md flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-teal-400 text-base"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Email</p>
                    <a
                      href="mailto:info@thornhilltreatment.com"
                      className="text-sm text-white hover:text-teal-400 transition-colors cursor-pointer break-all"
                    >
                      info@thornhilltreatment.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Directions CTA */}
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h4 className="text-sm font-bold text-gray-900 mb-1">
                Find Us on the Map
              </h4>
              <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                Conveniently located on Yonge St with free parking and easy transit access.
              </p>
              <a
                href="https://www.google.com/maps/dir//8108+Yonge+St+Unit+3-4,+Thornhill,+ON+L4J+1W4"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-navigation-line mr-2"></i>
                Get Directions
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
